import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Phone, Mail, MapPin } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { AddressLink } from "@/components/shared/AddressLink";
import { WhatsAppPhoneLink } from "@/components/ui/whatsapp-phone-link";
import { PetWeightHistoryCard, type PetWeightWithMeta } from "@/components/pets/PetWeightHistoryCard";
import { PetDaycareHotelSummary } from "@/components/pets/daycare-hotel/PetDaycareHotelSummary";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";

interface WeightChartPoint {
  date: string;
  weight: number;
}

interface OwnerPhone {
  id?: string | null;
  phone_number?: string | null;
  is_primary?: boolean | null;
  is_whatsapp?: boolean | null;
}

interface OwnerEmail {
  id?: string | null;
  email?: string | null;
  is_primary?: boolean | null;
}

interface OwnerAddress {
  id?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  zip_code?: string | null;
  city?: { name?: string | null; state?: { name?: string | null } | null } | null;
}

interface MobilePetOverviewTabProps {
  petId: string;
  petName: string;
  statusName: string | null | undefined;
  speciesName: string | null | undefined;
  genderName: string | null | undefined;
  sizeCategoryName: string | null | undefined;
  ageLabel: string | null | undefined;
  recordNumber: string | null | undefined;
  raceName: string | null | undefined;
  birthDateLabel: string | null | undefined;
  currentWeightValue: number | string | null | undefined;
  lastWeightDateLabel: string | null | undefined;
  weightChartData: WeightChartPoint[];
  petWeights: PetWeightWithMeta[];
  clearConsultationWeight?: (medicalRecordId: string) => Promise<void>;
  ownerId: string | null | undefined;
  ownerName: string | null | undefined;
  ownerPrimaryPhone: OwnerPhone | null;
  ownerPrimaryEmail: OwnerEmail | null;
  ownerAddresses: OwnerAddress[];
  petPhotoPath?: string | null;
  editButton?: React.ReactNode;
}

const CARD = "bg-card border border-border/60 rounded-[20px] p-[18px] shadow-soft-card";
const LABEL = "text-[11px] uppercase tracking-[0.06em] text-muted-foreground/70 font-semibold mb-[3px]";
const VALUE = "text-[14px] font-semibold text-foreground";

export function MobilePetOverviewTab({
  petId,
  petName,
  statusName,
  speciesName,
  genderName,
  sizeCategoryName,
  ageLabel,
  recordNumber,
  raceName,
  birthDateLabel,
  currentWeightValue,
  lastWeightDateLabel,
  weightChartData,
  petWeights,
  clearConsultationWeight,
  ownerId,
  ownerName,
  ownerPrimaryPhone,
  ownerPrimaryEmail,
  ownerAddresses,
  petPhotoPath,
  editButton,
}: MobilePetOverviewTabProps) {
  const { t } = useTranslation("patients");

  return (
    <div className="flex flex-col gap-3.5">

      {/* Identity card */}
      <div className={`${CARD} flex items-center gap-3.5`}>
        <Avatar className="h-[60px] w-[60px] flex-none rounded-full">
          <AvatarImage src={petPhotoPath ?? undefined} alt={petName} className="object-cover" />
          <AvatarFallback className="bg-secondary text-primary">
            <CustomIcons name="paws" className="h-[30px] w-[30px] text-current" />
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="font-extrabold text-[20px] tracking-tight text-foreground"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {petName}
            </span>
            {statusName && (
              <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 text-[11px] font-semibold px-2.5 py-[3px] rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-green-600" />
                {statusName}
              </span>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {[speciesName, genderName, sizeCategoryName, ageLabel].filter(Boolean).map((chip) => (
              <span
                key={String(chip)}
                className="text-[11px] font-medium text-foreground/80 bg-background-app border border-border/50 px-2.5 py-[3px] rounded-full whitespace-nowrap"
              >
                {chip}
              </span>
            ))}
          </div>
        </div>
        {editButton ? <div className="flex-none self-start">{editButton}</div> : null}
      </div>

      {/* Patient data grid */}
      <div className={CARD}>
        <p
          className="font-bold text-[15px] mb-3.5 text-foreground"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          {t("petProfile.mobileDataTitle")}
        </p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-4">
          {[
            { label: t("petProfile.fields.recordNumber"), value: recordNumber },
            { label: t("petProfile.fields.species"), value: speciesName },
            { label: t("petProfile.fields.gender"), value: genderName },
            { label: t("petProfile.fields.sizeCategory"), value: sizeCategoryName },
            { label: t("petProfile.fields.breed"), value: raceName },
            { label: t("petProfile.fields.birthDate"), value: birthDateLabel },
            { label: t("petProfile.fields.age"), value: ageLabel },
          ].map(({ label, value }) =>
            value ? (
              <div key={label}>
                <div className={LABEL}>{label}</div>
                <div className={VALUE}>{value}</div>
              </div>
            ) : null
          )}
        </div>
      </div>

      {/* Weight card */}
      {(currentWeightValue != null || weightChartData.length > 0) && (
        <div className={CARD}>
          <div className="flex items-center justify-between mb-3.5">
            <p
              className="font-bold text-[15px] text-foreground"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {t("petProfile.fields.weight")}
            </p>
          </div>
          {currentWeightValue != null && (
            <div className="flex items-end gap-2.5 mb-1">
              <span
                className="font-extrabold text-[30px] leading-none tracking-tight text-foreground"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {String(currentWeightValue).replace(".", ",")}
                <span className="text-[15px] font-semibold text-muted-foreground ml-1">kg</span>
              </span>
            </div>
          )}
          {lastWeightDateLabel && (
            <p className="text-[12px] text-muted-foreground mb-3">
              {t("petProfile.fields.lastMeasurementOn", { date: lastWeightDateLabel })}
            </p>
          )}
          {weightChartData.length >= 2 && (
            <ChartContainer
              config={{
                weight: {
                  label: t("petProfile.fields.weightChartLabel"),
                  color: "hsl(var(--primary))",
                },
              }}
              className="h-[128px] w-full aspect-auto"
            >
              <LineChart data={weightChartData} margin={{ left: 0, right: 18, top: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="date"
                  tickMargin={8}
                  minTickGap={12}
                  padding={{ left: 6, right: 10 }}
                  tick={{ fontSize: 9 }}
                />
                <YAxis width={32} tick={{ fontSize: 9 }} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Line
                  type="monotone"
                  dataKey="weight"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2.4}
                  dot={{
                    fill: "hsl(var(--card))",
                    stroke: "hsl(var(--primary))",
                    strokeWidth: 2,
                    r: 3.4,
                  }}
                  activeDot={{ r: 4, fill: "hsl(var(--primary))" }}
                />
              </LineChart>
            </ChartContainer>
          )}
        </div>
      )}

      {/* Weight history card */}
      <PetWeightHistoryCard
        petId={petId}
        weights={petWeights}
        clearConsultationWeight={clearConsultationWeight}
      />

      {/* Daycare & hotel summary */}
      <PetDaycareHotelSummary petId={petId} className={`${CARD} border-border/60`} />

      {/* Owner card */}
      {ownerName && (
        <div className={CARD}>
          <div className="flex items-center justify-between mb-3.5">
            <p
              className="font-bold text-[15px] text-foreground"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {t("petProfile.fields.owner")}
            </p>
            {ownerId && (
              <Link
                to={`/patients/owners/${ownerId}`}
                className="text-[13px] font-semibold text-foreground bg-background-app border border-border/60 px-3 py-[7px] rounded-full"
              >
                {t("petProfile.fields.viewOwner")}
              </Link>
            )}
          </div>
          <p className="text-[16px] font-bold text-foreground mb-3">{ownerName}</p>
          <div className="flex flex-col gap-[11px]">
            {ownerPrimaryPhone && (
              <div className="flex items-center gap-2.5">
                <span className="flex-none w-8 h-8 rounded-full bg-background-app flex items-center justify-center">
                  <Phone className="h-[15px] w-[15px] text-foreground/70" />
                </span>
                {ownerPrimaryPhone.is_whatsapp ? (
                  <WhatsAppPhoneLink
                    phoneNumber={ownerPrimaryPhone.phone_number}
                    className="flex-1 min-w-0"
                    textClassName="text-[14px] font-medium text-foreground truncate"
                  />
                ) : (
                  <span className="flex-1 text-[14px] font-medium text-foreground">
                    {ownerPrimaryPhone.phone_number}
                  </span>
                )}
              </div>
            )}
            {ownerPrimaryEmail?.email && (
              <div className="flex items-center gap-2.5">
                <span className="flex-none w-8 h-8 rounded-full bg-background-app flex items-center justify-center">
                  <Mail className="h-[15px] w-[15px] text-foreground/70" />
                </span>
                <span className="flex-1 text-[14px] font-medium text-foreground break-all">
                  {ownerPrimaryEmail.email}
                </span>
              </div>
            )}
            {ownerAddresses.length > 0 && (
              <div className="flex items-start gap-2.5">
                <span className="flex-none w-8 h-8 rounded-full bg-background-app flex items-center justify-center">
                  <MapPin className="h-[15px] w-[15px] text-foreground/70" />
                </span>
                <div className="flex-1">
                  {ownerAddresses.slice(0, 1).map((addr) => {
                    const parts = [
                      addr.street,
                      addr.number,
                      addr.complement,
                      addr.neighborhood,
                      addr.zip_code,
                    ].filter(Boolean);
                    const addressText = [parts.join(", "), addr.city?.name].filter(Boolean).join(", ");
                    return (
                      <AddressLink key={addr.id ?? addr.street} addressText={addressText}>
                        <span className="text-[14px] font-medium leading-[1.45] whitespace-pre-line">
                          {parts.join(", ")}
                          {addr.city?.name ? `\n${addr.city.name}` : ""}
                        </span>
                      </AddressLink>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
